/**
 * ATSScoreWidget.jsx
 *
 * Persistent, live-updating ATS score widget.
 * Renders in three contexts:
 *   variant="sidebar"  — full widget in the desktop left sidebar
 *   variant="header"   — compact pill in the sticky header
 *   variant="mobile"   — floating bottom-right button that opens a drawer
 *
 * Features:
 *   • Animated SVG score ring (CSS transition on stroke-dashoffset)
 *   • Score-up flash animation when score increases
 *   • 5 strength levels: Starter → Building → Strong → Expert → Recruiter Ready
 *   • Breakdown bars: Keywords / Sections / Metrics / Format
 *   • Top 4 missing keyword chips (clickable → navigates to Skills step)
 *   • Quantification hint when bullets lack numbers
 *   • Next action card — single most impactful thing to do right now
 */

import React, { useEffect, useRef, useState, memo } from 'react';
import {
  TrendingUp, ChevronDown, ChevronUp, X,
  AlertCircle, CheckCircle, Zap, Target,
} from 'lucide-react';
import { analyzeJobDescription, getKeywordType } from '../../../utils/aiResumeEngine';
import HighlightedJDText from './HighlightedJDText';

// ── Strength level config ─────────────────────────────────────────────────────
const STRENGTH_LEVELS = [
  { min: 0,  max: 20,  label: 'Starter',         color: '#ef4444', bg: 'bg-red-500',    ring: '#ef4444', text: 'text-red-600',    badge: 'bg-red-100 text-red-700',    emoji: '🌱' },
  { min: 21, max: 40,  label: 'Building',         color: '#f97316', bg: 'bg-orange-500', ring: '#f97316', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700', emoji: '🔨' },
  { min: 41, max: 60,  label: 'Strong',           color: '#eab308', bg: 'bg-yellow-500', ring: '#eab308', text: 'text-yellow-600', badge: 'bg-yellow-100 text-yellow-700', emoji: '💪' },
  { min: 61, max: 80,  label: 'Expert',           color: '#3b82f6', bg: 'bg-blue-500',   ring: '#3b82f6', text: 'text-blue-600',   badge: 'bg-blue-100 text-blue-700',   emoji: '🎯' },
  { min: 81, max: 100, label: 'Recruiter Ready',  color: '#22c55e', bg: 'bg-green-500',  ring: '#22c55e', text: 'text-green-600',  badge: 'bg-green-100 text-green-700', emoji: '⭐' },
];

const getLevel = (score) =>
  STRENGTH_LEVELS.find(l => score >= l.min && score <= l.max) || STRENGTH_LEVELS[0];

// ── Animated SVG ring ─────────────────────────────────────────────────────────
const ScoreRing = memo(({ score, size = 88, strokeWidth = 7, flash }) => {
  const r          = (size - strokeWidth) / 2;
  const circ       = 2 * Math.PI * r;
  const offset     = circ - (score / 100) * circ;
  const level      = getLevel(score);
  const cx         = size / 2;

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Track */}
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
        {/* Progress */}
        <circle
          cx={cx} cy={cx} r={r}
          fill="none"
          stroke={level.ring}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.4,0,0.2,1), stroke 0.4s ease' }}
        />
      </svg>
      {/* Score text */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${flash ? 'animate-pulse' : ''}`}>
        <span className={`font-black tabular-nums leading-none ${size >= 80 ? 'text-2xl' : 'text-lg'} ${level.text}`}>
          {score}
        </span>
        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-none mt-0.5">
          /100
        </span>
      </div>
    </div>
  );
});
ScoreRing.displayName = 'ScoreRing';

// ── Breakdown bar row ─────────────────────────────────────────────────────────
const BreakdownBar = ({ label, score, max, color }) => {
  const pct = Math.round((score / max) * 100);
  return (
    <div className="mb-2.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-600">{label}</span>
        <span className="text-xs font-bold text-gray-800 tabular-nums">{score}<span className="text-gray-400 font-normal">/{max}</span></span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5">
        <div
          className="h-1.5 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

// ── Next action card ──────────────────────────────────────────────────────────
const NextAction = ({ suggestions, onNavigate }) => {
  const top = suggestions?.[0];
  if (!top) return null;

  const priorityStyle = {
    critical: 'border-red-200 bg-red-50',
    high:     'border-orange-200 bg-orange-50',
    medium:   'border-yellow-200 bg-yellow-50',
    low:      'border-blue-200 bg-blue-50',
  }[top.priority] || 'border-gray-200 bg-gray-50';

  const priorityText = {
    critical: 'text-red-700',
    high:     'text-orange-700',
    medium:   'text-yellow-700',
    low:      'text-blue-700',
  }[top.priority] || 'text-gray-700';

  return (
    <div className={`rounded-xl border p-3 mt-3 ${priorityStyle}`}>
      <div className="flex items-start gap-2">
        <Zap className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${priorityText}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-bold uppercase tracking-wide mb-0.5 ${priorityText}`}>
            Next action
          </div>
          <p className="text-xs text-gray-700 leading-relaxed">{top.text}</p>
        </div>
      </div>
    </div>
  );
};

// ── Missing keyword chips ─────────────────────────────────────────────────────
const MissingKeywords = ({ keywordSuggestions, onNavigateToSkills }) => {
  const allMissing = Object.values(keywordSuggestions || {}).flat().slice(0, 5);
  if (!allMissing.length) return null;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 mb-2">
        <AlertCircle className="w-3 h-3 text-orange-500" />
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Missing keywords</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {allMissing.map(kw => (
          <button
            key={kw}
            type="button"
            onClick={onNavigateToSkills}
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 transition-colors"
          >
            + {kw}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Quantification hint ───────────────────────────────────────────────────────
const QuantHint = ({ bulletsWithMetrics, totalBullets }) => {
  if (bulletsWithMetrics >= 3 || totalBullets === 0) return null;
  return (
    <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
      <Target className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-amber-800 leading-relaxed">
        <strong>{bulletsWithMetrics}/{totalBullets}</strong> bullets have numbers.
        Add metrics like <em>"reduced bugs by 60%"</em> to boost your score.
      </p>
    </div>
  );
};

// ── Strength level progress strip ────────────────────────────────────────────
const StrengthStrip = ({ score }) => {
  const currentIdx = STRENGTH_LEVELS.findIndex(l => score >= l.min && score <= l.max);
  return (
    <div className="mt-3">
      <div className="flex gap-1 mb-1.5">
        {STRENGTH_LEVELS.map((l, i) => (
          <div
            key={l.label}
            className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${
              i <= currentIdx ? l.bg : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-400">Starter</span>
        <span className="text-[10px] text-gray-400">Recruiter Ready</span>
      </div>
    </div>
  );
};

// ── Job Description Matcher Component ──────────────────────────────────────────
const JobDescriptionMatcher = ({ formData, injectKeyword }) => {
  const [jdText, setJdText] = useState('');
  const [inputType, setInputType] = useState('text');
  const [jdUrl, setJdUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleFetchUrl = async () => {
    if (!jdUrl.trim()) return;
    setExtracting(true);
    setErrorMsg('');
    try {
      const res = await fetch('/api/resume-router?action=scrape-jd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: jdUrl.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || 'Failed to extract JD.');
        setInputType('text');
      } else {
        setJdText(data.text);
      }
    } catch (err) {
      setErrorMsg('Network error.');
      setInputType('text');
    } finally {
      setExtracting(false);
    }
  };

  useEffect(() => {
    if (!jdText.trim()) {
      setAnalysis(null);
      return;
    }
    const result = analyzeJobDescription(jdText, {
      skills: Object.values(formData?.technicalSkills || {}).flat(),
      tools: Object.values(formData?.tools || {}).flat(),
      certifications: (formData?.certifications || []).map(c => c.name).filter(Boolean),
      yearsExperience: 0,
    });
    setAnalysis(result);
  }, [jdText, formData]);

  const scoreColor = analysis?.matchScore >= 80 ? 'text-green-600' : analysis?.matchScore >= 60 ? 'text-yellow-600' : 'text-red-600';
  const barBg = analysis?.matchScore >= 80 ? 'bg-green-500' : analysis?.matchScore >= 60 ? 'bg-yellow-500' : 'bg-red-500';

  const sortedMissing = analysis?.uniqMissing
    ? [...analysis.uniqMissing].sort((a, b) => {
        const typeA = getKeywordType(a);
        const typeB = getKeywordType(b);
        if (typeA === 'tool' && typeB !== 'tool') return -1;
        if (typeA !== 'tool' && typeB === 'tool') return 1;
        return a.localeCompare(b);
      })
    : [];

  return (
    <div className="bg-gray-50/50 rounded-xl border border-gray-100 p-3 mt-3">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between font-bold text-[10px] uppercase tracking-wide text-gray-500"
      >
        <span className="flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5 text-blue-500" />
          Target Job Matcher
        </span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {!analysis ? (
            <div>
              {/* Toggle Text vs URL */}
              <div className="flex bg-gray-100 p-1 rounded-lg mb-3 w-full">
                <button
                  onClick={() => setInputType('text')}
                  className={`flex-1 py-1 text-[10px] font-bold rounded transition-all ${
                    inputType === 'text' ? 'bg-white shadow text-blue-600' : 'text-gray-500'
                  }`}
                >
                  Paste Text
                </button>
                <button
                  onClick={() => setInputType('url')}
                  className={`flex-1 py-1 text-[10px] font-bold rounded transition-all ${
                    inputType === 'url' ? 'bg-white shadow text-blue-600' : 'text-gray-500'
                  }`}
                >
                  Paste URL
                </button>
              </div>

              {errorMsg && (
                <div className="mb-3 p-2 bg-red-50 text-red-600 text-[10px] rounded border border-red-100 flex items-start gap-1">
                  <span>⚠️</span>
                  <span>{errorMsg}</span>
                </div>
              )}

              {inputType === 'text' ? (
                <textarea
                  value={jdText}
                  onChange={e => setJdText(e.target.value)}
                  placeholder="Paste target job description to scan keyword matches & calculate ATS match score..."
                  rows={4}
                  className="w-full p-2.5 bg-white border border-gray-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none placeholder-gray-400"
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <input
                    type="url"
                    value={jdUrl}
                    onChange={e => setJdUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleFetchUrl()}
                    placeholder="https://linkedin.com/jobs/..."
                    className="w-full p-2.5 bg-white border border-gray-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder-gray-400"
                  />
                  <button
                    onClick={handleFetchUrl}
                    disabled={!jdUrl.trim() || extracting}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-[10px] rounded-lg transition-colors disabled:opacity-50"
                  >
                    {extracting ? 'Extracting...' : 'Fetch & Analyze'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Match Score</span>
                  <div className="text-xs text-gray-500 font-medium">Seniority: {analysis.seniority || 'N/A'}</div>
                </div>
                <div className="text-right">
                  <span className={`text-xl font-black ${scoreColor}`}>{analysis.matchScore}%</span>
                  <span className={`text-xs font-bold block ${scoreColor}`}>Grade {analysis.grade}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4">
                <div className={`h-1.5 rounded-full ${barBg} transition-all duration-500`} style={{ width: `${analysis.matchScore}%` }} />
              </div>

              <HighlightedJDText 
                text={jdText}
                matched={analysis.matched}
                missing={analysis.uniqMissing}
              />

              {analysis.uniqMissing && analysis.uniqMissing.length > 0 ? (
                <div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                    Missing Keywords (Click to add)
                  </span>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto pr-1">
                    {sortedMissing.slice(0, 50).map(kw => (
                      <button
                        key={kw}
                        type="button"
                        onClick={() => injectKeyword(kw, { aiCategory: analysis?.categoryMap?.[kw] || analysis?.categoryMap?.[kw.toLowerCase()] })}
                        className="text-[9px] font-semibold px-2 py-0.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-100 hover:border-blue-200 transition-colors animate-fadeIn"
                      >
                        + {kw}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-2 px-1 bg-green-50 rounded-lg border border-green-100">
                  <span className="text-[10px] text-green-800 font-medium block">🎉 100% Keywords Matched!</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => setJdText('')}
                className="w-full text-center text-[10px] text-gray-400 hover:text-gray-600 font-semibold pt-1 transition-colors"
              >
                Reset / Paste New JD
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VARIANT: SIDEBAR — full widget for desktop left panel
// ─────────────────────────────────────────────────────────────────────────────
export const ATSScoreSidebar = memo(({ atsResult, onNavigateToSkills, formData, injectKeyword, isReviewStep }) => {
  const score      = atsResult?.total ?? 0;
  const level      = getLevel(score);
  const bd         = atsResult?.breakdown;
  const prevScore  = useRef(score);
  const [flash, setFlash] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Auto-collapse when reaching the review step so sections menu is visible
  useEffect(() => {
    if (isReviewStep) {
      setExpanded(false);
    }
  }, [isReviewStep]);

  // Flash animation when score increases
  useEffect(() => {
    if (score > prevScore.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      prevScore.current = score;
      return () => clearTimeout(t);
    }
    prevScore.current = score;
  }, [score]);

  return (
    <div className={`rounded-2xl border-2 p-4 transition-all duration-500 ${
      flash ? 'border-green-400 shadow-lg shadow-green-100' : 'border-gray-200'
    }`}>
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">ATS Score</span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Score ring + level */}
      <div className="flex items-center gap-3 mb-3">
        <ScoreRing score={score} size={80} strokeWidth={7} flash={flash} />
        <div className="flex-1 min-w-0">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-1 ${level.badge}`}>
            <span>{level.emoji}</span>
            <span>{level.label}</span>
          </div>
          <p className="text-[11px] text-gray-500 leading-tight">
            {score < 40  && 'Add skills & experience to improve'}
            {score >= 40 && score < 60  && 'Good start — add more keywords'}
            {score >= 60 && score < 80  && 'Strong resume — polish the details'}
            {score >= 80 && score < 95  && 'Excellent — almost recruiter-ready!'}
            {score >= 95 && 'Outstanding — top 5% of QA resumes!'}
          </p>
        </div>
      </div>

      {/* Strength strip */}
      <StrengthStrip score={score} />

      {/* Expandable detail */}
      {expanded && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          {/* Breakdown bars */}
          {bd && (
            <div className="mb-1">
              <BreakdownBar label="Keywords"  score={bd.keywords.score}  max={bd.keywords.max}  color="#3b82f6" />
              <BreakdownBar label="Sections"  score={bd.sections.score}  max={bd.sections.max}  color="#8b5cf6" />
              <BreakdownBar label="Metrics"   score={bd.metrics.score}   max={bd.metrics.max}   color="#f59e0b" />
              <BreakdownBar label="Format"    score={bd.format.score}    max={bd.format.max}    color="#10b981" />
            </div>
          )}

          {/* Quant hint */}
          {bd && (
            <QuantHint
              bulletsWithMetrics={bd.metrics.bulletsWithMetrics}
              totalBullets={bd.metrics.totalBullets}
            />
          )}

          {/* Missing keywords */}
          <MissingKeywords
            keywordSuggestions={atsResult?.keywordSuggestions}
            onNavigateToSkills={onNavigateToSkills}
          />

          {/* Next action */}
          <NextAction suggestions={atsResult?.suggestions} />

          {/* Target Job Description Matcher */}
          <JobDescriptionMatcher formData={formData} injectKeyword={injectKeyword} />
        </div>
      )}
    </div>
  );
});
ATSScoreSidebar.displayName = 'ATSScoreSidebar';

// ─────────────────────────────────────────────────────────────────────────────
// VARIANT: HEADER PILL — compact, shown in sticky top bar
// ─────────────────────────────────────────────────────────────────────────────
export const ATSScoreHeaderPill = memo(({ atsResult }) => {
  const score = atsResult?.total ?? 0;
  const level = getLevel(score);
  const prevScore = useRef(score);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (score > prevScore.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 800);
      prevScore.current = score;
      return () => clearTimeout(t);
    }
    prevScore.current = score;
  }, [score]);

  if (score === 0) return null;

  return (
    <div className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all duration-300 ${
      flash
        ? 'border-green-400 bg-green-50 scale-105'
        : `border-transparent ${level.badge}`
    }`}>
      <div className="relative w-4 h-4 flex-shrink-0">
        <svg width="16" height="16" className="-rotate-90">
          <circle cx="8" cy="8" r="5.5" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
          <circle
            cx="8" cy="8" r="5.5"
            fill="none"
            stroke={level.ring}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={34.6}
            strokeDashoffset={34.6 - (score / 100) * 34.6}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
      </div>
      <span className={`text-xs font-black tabular-nums ${level.text}`}>{score}</span>
      <span className="text-[10px] font-bold text-gray-400 hidden md:inline">{level.emoji} {level.label}</span>
    </div>
  );
});
ATSScoreHeaderPill.displayName = 'ATSScoreHeaderPill';

// ─────────────────────────────────────────────────────────────────────────────
// VARIANT: MOBILE FLOATING BUTTON + DRAWER
// ─────────────────────────────────────────────────────────────────────────────
export const ATSScoreMobileDrawer = memo(({ atsResult, onNavigateToSkills, formData, injectKeyword }) => {
  const [open, setOpen] = useState(false);
  const score = atsResult?.total ?? 0;
  const level = getLevel(score);
  const bd    = atsResult?.breakdown;
  const prevScore = useRef(score);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (score > prevScore.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1000);
      prevScore.current = score;
      return () => clearTimeout(t);
    }
    prevScore.current = score;
  }, [score]);

  if (score === 0) return null;

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`lg:hidden fixed bottom-24 right-4 z-30 flex items-center gap-2 px-3.5 py-2.5 rounded-2xl shadow-xl border-2 transition-all duration-300 ${
          flash
            ? 'border-green-400 bg-green-50 scale-110'
            : 'border-white bg-white'
        }`}
        style={{ boxShadow: `0 4px 20px ${level.ring}40` }}
      >
        <ScoreRing score={score} size={36} strokeWidth={4} flash={flash} />
        <div className="text-left">
          <div className={`text-[10px] font-bold uppercase tracking-wide ${level.text}`}>ATS Score</div>
          <div className="text-[10px] text-gray-500 font-medium">{level.emoji} {level.label}</div>
        </div>
      </button>

      {/* Drawer overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white w-full rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-8 pt-2">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">ATS Score</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Score + level */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-2xl">
                <ScoreRing score={score} size={88} strokeWidth={8} flash={flash} />
                <div>
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold mb-2 ${level.badge}`}>
                    <span>{level.emoji}</span>
                    <span>{level.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed max-w-[180px]">
                    {score < 40  && 'Add skills & experience to improve your score'}
                    {score >= 40 && score < 60  && 'Good start — add more QA keywords'}
                    {score >= 60 && score < 80  && 'Strong resume — polish the details'}
                    {score >= 80 && score < 95  && 'Excellent — almost recruiter-ready!'}
                    {score >= 95 && 'Outstanding — top 5% of QA resumes!'}
                  </p>
                </div>
              </div>

              {/* Strength strip */}
              <StrengthStrip score={score} />

              {/* Breakdown */}
              {bd && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Score Breakdown</h4>
                  <BreakdownBar label="Keywords"  score={bd.keywords.score}  max={bd.keywords.max}  color="#3b82f6" />
                  <BreakdownBar label="Sections"  score={bd.sections.score}  max={bd.sections.max}  color="#8b5cf6" />
                  <BreakdownBar label="Metrics"   score={bd.metrics.score}   max={bd.metrics.max}   color="#f59e0b" />
                  <BreakdownBar label="Format"    score={bd.format.score}    max={bd.format.max}    color="#10b981" />
                </div>
              )}

              {/* Quant hint */}
              {bd && (
                <QuantHint
                  bulletsWithMetrics={bd.metrics.bulletsWithMetrics}
                  totalBullets={bd.metrics.totalBullets}
                />
              )}

              {/* Missing keywords */}
              <MissingKeywords
                keywordSuggestions={atsResult?.keywordSuggestions}
                onNavigateToSkills={() => { onNavigateToSkills?.(); setOpen(false); }}
              />

              {/* Next action */}
              <NextAction suggestions={atsResult?.suggestions} />

              {/* Target Job Description Matcher */}
              <JobDescriptionMatcher formData={formData} injectKeyword={injectKeyword} />
            </div>
          </div>
        </div>
      )}
    </>
  );
});
ATSScoreMobileDrawer.displayName = 'ATSScoreMobileDrawer';
